/* eslint-disable prettier/prettier */
/* eslint-disable no-undef */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { API } from '../../../api';
// import { error } from 'node:console';

export interface IArticleData {
  articleId: string;
  orgId: string;
  groups: string[];
  articleName: string;
  type: string;
  coverImageUrl: string;
  fileUrl: string;
  authorName: string;
  authorId: string;
  createdBy?: string;
  readTime: string;
  accessFor: string;
  articleViewableTo: string;
  articleViewableUserType: string[];
  tags: string[];
  industries: string[];
  articleDescription: string;
  embeddedLinks: string;
  approved: boolean;
  upVotes: number;
  downVotes: number;
  views: number;
  isPublished: boolean;
  allowedGroup: boolean;
  location?: string;
  createdAt: number;
  __v: number;
}

export interface IArticle {
  ArticleList: IArticleData[];
  total: number;
}

interface IInitialState {
  loading: boolean;
  data: IArticle | undefined;
  error: boolean;
  errorText: string;
}

const initialState: IInitialState = {
  loading: false,
  data: undefined,
  error: false,
  errorText: ''
};

const sortArticlesByMostRecent = (articles: IArticleData[] = []) =>
  [...articles].sort((a, b) => (b?.createdAt || 0) - (a?.createdAt || 0));

export const fetchAllArticles = createAsyncThunk(
  'getAllArticles',
  ({
    orgId,
    groupId,
    helpSettings,
    useCache,
    role,
    location
  }: {
    orgId: string;
    groupId: string;
    helpSettings: boolean;
    useCache?: boolean;
    role?: string;
    location?: string;
  }) => {
    // Org-level Resources page has no program in context, so groupId is empty. The
    // group-scoped endpoint rejects that (/group//getArticleList → 500 "Invalid Id
    // Format"). Fall back to the org-admin library endpoint, which lists org-wide +
    // All-of-Dosen resources without a group. Location is forwarded so EU orgs query
    // the EU database.
    const hasRealGroup = Boolean(groupId) && groupId !== 'admin';
    const request = hasRealGroup
      ? API.getAllArticle({ orgId, groupId, helpSettings, useCache, role, location })
      : // The admin Resources page manages unpublished resources too: they vanished from it once
        // hidden, so they could never be re-published or removed (QA ORG-55).
        API.getAllArticleUser({ orgId, helpSettings, useCache, location, includeUnpublished: true });
    return request
      .then((response) => response.data)
      .catch((error) => {
        throw error;
      });
  }
);

const getAllArticleSlice = createSlice({
  name: 'get all articles',
  initialState,
  reducers: {
    addArticle: (state: IInitialState, action) => {
      const ArticleList = state.data?.ArticleList || [];
      const total = state.data?.total || 0;
      return {
        ...state,
        data: {
          ArticleList: sortArticlesByMostRecent([...ArticleList, action.payload]),
          total: total + 1
        }
      };
    },
    deleteArticle: (state: IInitialState, action) => {
      const filteredArticles = state.data?.ArticleList.filter((each) => each.articleId !== action.payload) || [];
      const total = state.data?.total || 0;
      return {
        ...state,
        data: {
          ArticleList: filteredArticles,
          total: total - 1
        }
      };
    },
    updateArticle: (state: IInitialState, action) => {
      if (state.data) {
        const articleList = state.data.ArticleList.map((article) => {
          if (article.articleId === action.payload.articleId) {
            return action.payload;
          }
          return article;
        });

        return {
          ...state,
          data: {
            ...state.data,
            ArticleList: sortArticlesByMostRecent(articleList)
          }
        };
      }
      return state;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllArticles.pending, (state: IInitialState) => {
        state.loading = true;
        state.error = false;
        state.errorText = '';
        // state.data = undefined;
      })
      .addCase(fetchAllArticles.fulfilled, (state: IInitialState, action) => {
        state.loading = false;
        state.error = false;
        state.errorText = '';
        state.data = {
          ...(action.payload as IArticle),
          ArticleList: sortArticlesByMostRecent((action.payload as IArticle)?.ArticleList || [])
        };
      })
      .addCase(fetchAllArticles.rejected, (state: IInitialState, action) => {
        state.loading = false;
        state.error = true;
        // console.error(action.error);
        state.errorText = action.error.message || 'Something went wrong';
        state.data = undefined;
      });
  }
});

export const articleActions = getAllArticleSlice.actions;

export default getAllArticleSlice.reducer;
